import useInterpolation from './useInterpolation.tsx';
import { resolveMediaUrl, TCmsField } from '../cms';
import { TDateFormat } from '../components/inputs/dateInput.tsx';
import {
  Alert,
  FieldType,
  TAutoCompleteAddressInput,
  TBaseField,
  TCalloutField,
  TCardField,
  TConsentField,
  TCountrySelectorField,
  TDateInputField,
  TDependsOn,
  TDepositIncrementerField,
  TDepositLimitSelection,
  TDepositLimitsField,
  TDepositTypeSelectorField,
  TEmailInputField,
  TEnterAddressManuallyTextField,
  TEsPasswordInputField,
  TField,
  TIdentificationNumberESField,
  TIdentificationNumberESSelection,
  TIdentityProtectedPasswordField,
  TInputField,
  TMultiMarketingConsentField,
  TPasswordInputField,
  TPhoneNumberInputField,
  TPostcodeInputField,
  TProvinceSelectorField,
  TSelectorDrawerField,
  TSelectorField,
  TTextField,
  TWelcomeOfferField,
  TWoCalloutField,
} from '../types/types.ts';
import { ENVIRONMENTS } from '../utils/constants.ts';
import { REACT_APP_ENVIRONMENT } from '../utils/envConstants.ts';

const useFieldMapper = () => {
  const { text } = useInterpolation();

  const dependsOn = (field: TCmsField): TDependsOn[] | undefined =>
    field.dependsOn?.map(({ field: name, value, booleanValue }) => ({
      field: name ?? '',
      ...(value !== null && value !== undefined ? { value } : {}),
      ...(booleanValue !== null && booleanValue !== undefined ? { booleanValue } : {}),
    }));

  const validation = (field: TCmsField) => field.validation?.validation ?? undefined;

  const mapBaseField = (field: TCmsField): TBaseField => ({
    name: field.fieldName?.name || field.name || '',
  });

  const mapCalloutField = (field: TCmsField): TCalloutField => ({
    ...mapBaseField(field),
    fieldType:   FieldType.Callout,
    label:       text(field.label),
    description: text(field.description),
    dependsOn:   dependsOn(field),
    alert:       Alert.Info,
  });

  const mapInputField = (field: TCmsField): TInputField => ({
    ...mapBaseField(field),
    fieldType:  FieldType.Input,
    type:       field.type ?? undefined,
    clearable:  field.clearable ?? undefined,
    label:      text(field.label),
    flex:       field.flex ?? undefined,
    dependsOn:  dependsOn(field),
    gtmPayload: field.gtmPayload ?? undefined,
    validation: validation(field),
  });

  const mapCardField = (field: TCmsField): TCardField => ({
    ...mapBaseField(field),
    fieldType:   FieldType.Card,
    label:       text(field.label),
    description: text(field.description),
    dependsOn:   dependsOn(field),
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    fields:      field.fields?.map(subField => mapField(subField)) || [],
  });

  const mapSelectorField = (field: TCmsField): TSelectorField => ({
    ...mapBaseField(field),
    fieldType:  FieldType.Selector,
    label:      text(field.label),
    validation: validation(field),
    options:    field.options?.map(({ label, value }) => ({ label, value })) || [],
  });

  const mapTextField = (field: TCmsField): TTextField => ({
    ...mapBaseField(field),
    label:      text(field.label),
    fieldType:  FieldType.Text,
    size:       field.size ?? undefined,
    textAlign:  field.textAlign ?? undefined,
    color:      field.color ?? undefined,
    fontWeight: field.fontWeight ?? undefined,
    dependsOn:  dependsOn(field),
  });

  const mapWelcomeOfferField = (field: TCmsField): TWelcomeOfferField => ({
    ...mapBaseField(field),
    name:      field.name ?? '',
    fieldType: FieldType.WelcomeOffer,
  });

  const mapCountrySelectorField = (field: TCmsField): TCountrySelectorField => ({
    ...mapBaseField(field),
    fieldType:   FieldType.CountrySelector,
    disabled:    field.disabled ?? undefined,
    label:       text(field.label),
    dialogTitle: text(field.dialogTitle),
    placeholder: text(field.placeholder),
    validation:  validation(field),
    dependsOn:   dependsOn(field),
  });

  const mapProvinceSelectorField = (field: TCmsField): TProvinceSelectorField => ({
    ...mapBaseField(field),
    fieldType:   FieldType.ProvinceSelector,
    disabled:    field.disabled ?? undefined,
    label:       text(field.label),
    dialogTitle: text(field.dialogTitle),
    placeholder: text(field.placeholder),
    validation:  validation(field),
    dependsOn:   dependsOn(field),
  });

  const mapSelectorDrawerField = (field: TCmsField): TSelectorDrawerField => ({
    ...mapBaseField(field),
    fieldType:   FieldType.SelectorDrawer,
    disabled:    field.disabled ?? undefined,
    label:       text(field.label),
    dialogTitle: text(field.dialogTitle),
    placeholder: text(field.placeholder),
    validation:  validation(field),
    dependsOn:   dependsOn(field),
  });

  const mapEnterAddressManuallyTextField = (field: TCmsField): TEnterAddressManuallyTextField => ({
    ...mapBaseField(field),
    fieldType:                  FieldType.EnterAddressManuallyText,
    name:                       field.name ?? '',
    label:                      text(field.label),
    link:                       text(field.link),
    toggleEnterAddressManually: field.toggleEnterAddressManually ?? false,
    dependsOn:                  dependsOn(field),
  });

  const mapPhoneNumberInputField = (field: TCmsField): TPhoneNumberInputField => ({
    ...mapBaseField(field),
    fieldType:   FieldType.PhoneNumberInput,
    label:       text(field.label),
    dialogTitle: text(field.dialogTitle),
    validation:  validation(field),
    gtmPayload:  field.gtmPayload ?? undefined,
  });

  const mapMultiMarketingConsentField = (field: TCmsField): TMultiMarketingConsentField => ({
    ...mapBaseField(field),
    fieldType:        FieldType.MultiMarketingConsent,
    label:            text(field.label),
    dialogTitle:      text(field.dialogTitle),
    title:            text(field.title),
    description:      text(field.description),
    casinoLabel:      text(field.casinoLabel),
    sportsLabel:      text(field.sportsLabel),
    smsLabel:         text(field.smsLabel),
    emailLabel:       text(field.emailLabel),
    phoneLabel:       text(field.phoneLabel),
    postLabel:        text(field.postLabel),
    pushLabel:        text(field.pushLabel),
    expandText:       text(field.expandText),
    closeText:        text(field.closeText),
    explanatoryText:  text(field.explanatoryText),
    verticalError:    text(field.vertical_error),
    verticalWarning:  text(field.vertical_warning),
    contactError:     text(field.contact_error),
    contactWarning:   text(field.contact_warning),
    verticalTitle:    text(field.vertical_title),
    contactTitle:     text(field.contact_title),
    verticalSubtitle: text(field.vertical_subtitle),
  });

  const mapEmailInputField = (field: TCmsField): TEmailInputField => ({
    ...mapBaseField(field),
    fieldType:             FieldType.EmailInput,
    label:                 text(field.label),
    dialogTitle:           text(field.dialogTitle),
    dialogDescription:     text(field.dialogDescription),
    primaryButtonTitle:    text(field.primaryButtonTitle),
    secondaryButtonTitle:  text(field.secondaryButtonTitle),
    clearsPasswordOnClear: field.clearsPasswordOnClear ?? undefined,
    autoFocused:           field.autoFocused ?? undefined,
    validation:            validation(field),
  });

  const mapAutoCompleteAddressInputField = (field: TCmsField): TAutoCompleteAddressInput => ({
    ...mapBaseField(field),
    fieldType:   FieldType.AutoCompleteAddressInput,
    label:       text(field.label),
    dialogTitle: text(field.dialogTitle),
  });

  const mapDateInputField = (field: TCmsField): TDateInputField => ({
    ...mapBaseField(field),
    fieldType:  FieldType.DateInput,
    label:      text(field.label),
    dateFormat: (field.dateFormat as TDateFormat) ?? undefined,
    minAge:     field.minAge ?? undefined,
  });

  const mapConsentField = (field: TCmsField): TConsentField => ({
    ...mapBaseField(field),
    fieldType:   FieldType.ConsentField,
    label:       text(field.label),
    textAlign:   field.textAlign ?? undefined,
    description: text(field.description),
    consents:    (field.consents ?? []).map(({ name, replaceWith, staticTermsName, link }) =>
      ({ name, replaceWith, staticTermsName, link })),
    isCheckable: field.isCheckable ?? undefined,
    title:       text(field.title),
    validation:  validation(field),
  });

  const mapWoCalloutField = (field: TCmsField): TWoCalloutField => ({
    ...mapBaseField(field),
    fieldType:     FieldType.WoCallout,
    alert:         Alert.Warning,
    label:         text(field.label),
    description:   text(field.description),
    dependsOn:     dependsOn(field),
    dialogTitle:   text(field.dialogTitle),
    noBonusSelect: text(field.noBonusSelect),
  });

  const mapDepositIncrementerField = (field: TCmsField): TDepositIncrementerField => ({
    ...mapBaseField(field),
    fieldType: FieldType.DepositIncrementer,
    label:     text(field.label),
    options:   field.options?.map(({ label, value }) => ({ label, value })) || [],
  });

  const mapDepositTypeSelectorField = (field: TCmsField): TDepositTypeSelectorField => ({
    ...mapBaseField(field),
    fieldType: FieldType.DepositTypeSelector,
    label:     text(field.label),
    options:   field.options?.map(({ label, value }) => ({ label, value })) || [],
  });

  const mapPostcodeInputField = (field: TCmsField): TPostcodeInputField => ({
    ...mapBaseField(field),
    fieldType:            FieldType.PostcodeInput,
    label:                text(field.label),
    dialogTitle:          text(field.dialogTitle),
    dialogDescription:    text(field.dialogDescription),
    primaryButtonTitle:   text(field.primaryButtonTitle),
    secondaryButtonTitle: text(field.secondaryButtonTitle),
    flex:                 field.flex ?? undefined,
    validation:           validation(field),
  });

  const mapDepositLimits = (field: TCmsField): TDepositLimitsField => ({
    ...mapBaseField(field),
    fieldType:  FieldType.DepositLimits,
    selections: (field.selections ?? []).map((selection) => {
      const { name, label, inputLabel } = selection as TDepositLimitSelection;

      return { name, label, inputLabel };
    }),
    requiresOptInConfirmation: field.requiresOptInConfirmation ?? undefined,
    noLimitOptionText:         text(field.noLimitOptionText),
    setLimitOptionText:        text(field.setLimitOptionText),
  });

  const mapIdentificationNumberES = (field: TCmsField): TIdentificationNumberESField => ({
    ...mapBaseField(field),
    fieldType:     FieldType.IdentificationNumberES,
    documentImage: resolveMediaUrl(field.documentImage),
    selections:    (field.selections ?? []).map((selection) => {
      const { name, labelInput, documentDescription, example } =
        selection as TIdentificationNumberESSelection;

      return { name, labelInput, documentDescription, example };
    }),
  });

  const mapIdentityProtectedPasswordField = (field: TCmsField): TIdentityProtectedPasswordField => ({
    ...mapBaseField(field),
    fieldType:          FieldType.IdentityProtectedPassword,
    label:              text(field.label),
    validation:         validation(field),
    instructionPartOne: text(field.instructionPartOne),
    instructionPartTwo: text(field.instructionPartTwo),
  });

  const mapPasswordInputField = (field: TCmsField): TPasswordInputField => ({
    ...mapBaseField(field),
    fieldType:            FieldType.PasswordInput,
    label:                text(field.label),
    validation:           validation(field),
    ruleMinLength:        text(field.ruleMinLength),
    ruleHasLetter:        text(field.ruleHasLetter),
    ruleHasNumber:        text(field.ruleHasNumber),
    ruleHasSpecialChar:   text(field.ruleHasSpecialChar),
    successMessage:       text(field.successMessage),
    errorMessage:         text(field.errorMessage),
    warningNote:          text(field.warningNote),
    validationPanelTitle: text(field.validationPanelTitle),
  });

  const mapEsPasswordInputField = (field: TCmsField): TEsPasswordInputField => ({
    ...mapBaseField(field),
    fieldType:                 FieldType.EsPasswordInput,
    label:                     text(field.label),
    validation:                validation(field),
    ruleMinLength:             text(field.ruleMinLength),
    ruleHasLetter:             text(field.ruleHasLetter),
    ruleHasNumber:             text(field.ruleHasNumber),
    ruleHasSpecialChar:        text(field.ruleHasSpecialChar),
    validationPanelTitle:      text(field.validationPanelTitle),
    simplePatternError:        text(field.simplePatternError),
    personalDetailsError:      text(field.personalDetailsError),
    patternAndPersonalError:   text(field.patternAndPersonalError),
    untickedRequirementsError: text(field.untickedRequirementsError),
    successMessage:            text(field.successMessage),
    warningNote:               text(field.warningNote),
  });

  const fieldMapper = {
    [FieldType.Callout]:                   mapCalloutField,
    [FieldType.Input]:                     mapInputField,
    [FieldType.Card]:                      mapCardField,
    [FieldType.Selector]:                  mapSelectorField,
    [FieldType.Text]:                      mapTextField,
    [FieldType.CountrySelector]:           mapCountrySelectorField,
    [FieldType.ProvinceSelector]:          mapProvinceSelectorField,
    [FieldType.SelectorDrawer]:            mapSelectorDrawerField,
    [FieldType.EnterAddressManuallyText]:  mapEnterAddressManuallyTextField,
    [FieldType.PhoneNumberInput]:          mapPhoneNumberInputField,
    [FieldType.EmailInput]:                mapEmailInputField,
    [FieldType.WelcomeOffer]:              mapWelcomeOfferField,
    [FieldType.AutoCompleteAddressInput]:  mapAutoCompleteAddressInputField,
    [FieldType.DateInput]:                 mapDateInputField,
    [FieldType.ConsentField]:              mapConsentField,
    [FieldType.WoCallout]:                 mapWoCalloutField,
    [FieldType.DepositIncrementer]:        mapDepositIncrementerField,
    [FieldType.DepositTypeSelector]:       mapDepositTypeSelectorField,
    [FieldType.DepositLimits]:             mapDepositLimits,
    [FieldType.PostcodeInput]:             mapPostcodeInputField,
    [FieldType.IdentityProtectedPassword]: mapIdentityProtectedPasswordField,
    [FieldType.IdentificationNumberES]:    mapIdentificationNumberES,
    [FieldType.MultiMarketingConsent]:     mapMultiMarketingConsentField,
    [FieldType.PasswordInput]:             mapPasswordInputField,
    [FieldType.EsPasswordInput]:           mapEsPasswordInputField,
  } as const;

  const mapField = <T extends FieldType>(
    field: TCmsField & { fieldType: T },
  ): ReturnType<typeof fieldMapper[T]> => {
    const mapper = fieldMapper[field.fieldType];

    if (
      !mapper
      && (REACT_APP_ENVIRONMENT === ENVIRONMENTS.DEV || REACT_APP_ENVIRONMENT === ENVIRONMENTS.TEST)
    ) {
      console.info('Field type not found:', { fieldName: field.fieldName, fieldType: field.fieldType });

      return <div className="text-red-50 font-semibold text-lg">🚧 Under Construction
        🚧</div> as ReturnType<typeof fieldMapper[T]>;
    }

    return mapper(field) as ReturnType<typeof fieldMapper[T]>;
  };

  return {
    mapField: mapField as (field: TCmsField) => TField,
  };
};

export default useFieldMapper;
